import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";

const submitExample = `curl --request POST \\
  --url https://gateway.llmasaservice.io/v1/batches \\
  --header "Authorization: Bearer $LLMAS_API_KEY" \\
  --header "Idempotency-Key: summaries-2026-07-23" \\
  --header "Content-Type: application/json" \\
  --data '{
    "callback_url": "https://example.com/webhooks/llm-batches",
    "requests": [
      {
        "custom_id": "summary-001",
        "body": {
          "model": "openai:gpt-4.1-mini",
          "stream": false,
          "max_completion_tokens": 500,
          "messages": [
            {
              "role": "user",
              "content": "Summarize this document..."
            }
          ],
          "customer": {
            "customer_id": "customer-123"
          }
        }
      }
    ]
  }'`;

const pollExample = `curl --request GET \\
  --url "https://api.llmasaservice.io/calls?batch_id=gbatch_..." \\
  --header "Authorization: Bearer $LLMAS_API_KEY"`;

const callbackExample = `import { createHmac, timingSafeEqual } from "node:crypto";

function verifyBatchCallback({ rawBody, timestamp, signature, secret }) {
  // Reject timestamps older than your replay window before accepting the event.
  const expected = "sha256=" + createHmac("sha256", secret)
    .update(\`\${timestamp}.\${rawBody}\`, "utf8")
    .digest("hex");

  const actualBytes = Buffer.from(signature);
  const expectedBytes = Buffer.from(expected);
  return actualBytes.length === expectedBytes.length &&
    timingSafeEqual(actualBytes, expectedBytes);
}

// Read these before parsing rawBody as JSON:
// x-llmas-webhook-id          - use for deduplication
// x-llmas-webhook-timestamp   - Unix timestamp
// x-llmas-webhook-signature   - sha256=<hex digest>`;

const responseExample = `{
  "id": "gbatch_...",
  "object": "batch",
  "status": "queued",
  "request_counts": {
    "total": 1,
    "completed": 0,
    "failed": 0
  },
  "calls": [
    {
      "custom_id": "summary-001",
      "call_id": "call_...",
      "status": 202
    }
  ],
  "callback_secret": "returned-only-on-initial-creation"
}`;

interface CodeSampleProps {
  label: string;
  code: string;
}

const CodeSample: React.FC<CodeSampleProps> = ({ label, code }) => {
  const copy = async () => {
    await navigator.clipboard?.writeText(code);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2">{label}</Typography>
        <Button size="small" onClick={copy}>
          Copy
        </Button>
      </Stack>
      <Box
        component="pre"
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.28)",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          fontSize: 12,
          m: 0,
          maxHeight: 430,
          overflow: "auto",
          p: 2,
          whiteSpace: "pre",
        }}
      >
        {code}
      </Box>
    </Box>
  );
};

const VendorBatchIntegration: React.FC = () => (
  <Paper
    elevation={3}
    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
  >
    <Box p={{ xs: 2, md: 3 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Typography variant="h5">Vendor Batch API</Typography>
            <Chip
              label="OpenAI + Anthropic"
              color="primary"
              size="small"
              variant="outlined"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Schedule up to 100 non-streaming requests as a true vendor batch.
            LLMGateway records each request as a normal call and automatically
            applies the configured vendor-batch token pricing.
          </Typography>
        </Box>
        <Button variant="outlined" href="/chat/batch">
          Open Batch Test
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mt: 2 }}>
        Submit batches directly to LLMGateway. Do not send vendor batch requests
        through the chat Lambda or its legacy asynchronous mode.
      </Alert>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} lg={7}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                1. Submit the batch
              </Typography>
              <Typography variant="body2" paragraph>
                Send <code>POST https://gateway.llmasaservice.io/v1/batches</code>.
                Use a project integration key from this page. The{" "}
                <code>Idempotency-Key</code> header is required: retry transient
                failures with the same key and identical payload. Reusing the key
                for a different payload returns HTTP 409.
              </Typography>
              <CodeSample label="Submit with cURL" code={submitExample} />
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                2. Store the acceptance response
              </Typography>
              <Typography variant="body2" paragraph>
                HTTP 202 returns a gateway batch ID and one call ID for every
                request. Store these IDs. If a callback URL was supplied, store
                the callback secret securely—it is returned only when the batch
                is initially created.
              </Typography>
              <CodeSample label="Example HTTP 202 response" code={responseExample} />
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                3. Poll for results
              </Typography>
              <Typography variant="body2" paragraph>
                Poll the existing public calls API using the batch ID, or retrieve
                an individual call with <code>GET /calls/{"{call_id}"}</code>.
                Polling is project-isolated and uses the same integration key.
              </Typography>
              <CodeSample label="Poll all calls in a batch" code={pollExample} />
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Authentication headers
              </Typography>
              <Typography variant="body2">
                Gateway submission:
              </Typography>
              <Box component="ul" sx={{ mt: 0.5 }}>
                <li><code>Authorization: Bearer &lt;integration-key&gt;</code></li>
                <li><code>Idempotency-Key: &lt;unique-operation-key&gt;</code></li>
              </Box>
              <Typography variant="body2">
                Public calls API polling:
              </Typography>
              <Box component="ul" sx={{ mt: 0.5 }}>
                <li><code>Authorization: Bearer &lt;integration-key&gt;</code></li>
              </Box>
              <Typography variant="body2" color="text.secondary">
                CORS applies only to browser clients. It is not an authentication
                mechanism; keep integration keys out of untrusted frontend code.
                Server-to-server callers are not subject to browser CORS checks.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Request requirements
              </Typography>
              <Box component="ul" sx={{ mt: 0.5 }}>
                <li>1–100 requests per Gateway submission.</li>
                <li>Maximum 100 KiB per request and 4 MiB total.</li>
                <li>Unique <code>custom_id</code> values, up to 64 characters.</li>
                <li>
                  All requests must resolve to one batch-priced OpenAI or
                  Anthropic vendor.
                </li>
                <li><code>messages</code> is required and streaming is unsupported.</li>
                <li>Callbacks must use a public HTTPS URL.</li>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Status and cost
              </Typography>
              <Box component="ul" sx={{ mt: 0.5 }}>
                <li>Call status <code>202</code>: pending.</li>
                <li>Call status <code>200</code>: completed successfully.</li>
                <li>Other call statuses are terminal failures.</li>
                <li>
                  Terminal records include response or error, input/output tokens,
                  vendor status, vendor IDs, cost, and{" "}
                  <code>pricingMode: "batch"</code>. The execution path is
                  identified separately by{" "}
                  <code>executionMode: "vendor_batch"</code>.
                </li>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Vendor batches are asynchronous and may take up to 24 hours.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Optional signed callback
              </Typography>
              <Typography variant="body2" paragraph>
                Verify the HMAC against the exact raw request body before parsing
                JSON. Reject stale timestamps and deduplicate deliveries using the
                webhook ID. Return any 2xx response only after the event is safely
                recorded.
              </Typography>
              <CodeSample label="Node.js signature verification" code={callbackExample} />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  </Paper>
);

export default VendorBatchIntegration;
